import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Check, X, BookmarkCheck, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approuvée', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejetée', color: 'bg-red-100 text-red-700' },
};

export default function ManageReservations() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/stand-reservations`, { headers });
      setItems(res.data);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await axios.put(`${API}/stand-reservations/${id}/approve`, {}, { headers });
      toast.success('Réservation approuvée');
      fetchItems();
    } catch (e) { toast.error('Erreur'); }
    finally { setProcessing(null); }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Rejeter cette réservation ?')) return;
    setProcessing(id);
    try {
      await axios.put(`${API}/stand-reservations/${id}/reject`, {}, { headers });
      toast.success('Réservation rejetée');
      fetchItems();
    } catch (e) { toast.error('Erreur'); }
    finally { setProcessing(null); }
  };

  const pending = items.filter(i => i.status === 'pending').length;

  return (
    <div data-testid="manage-reservations">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">Réservations de stands</h1>
          <p className="text-sm text-[#6B7C93]">{items.length} réservation(s) — {pending} en attente</p>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Stand</TableHead>
                <TableHead>Entreprise</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Téléphone</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="w-28">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const st = STATUS_MAP[item.status] || STATUS_MAP.pending;
                return (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono font-bold">{item.stand_number || '-'}</TableCell>
                    <TableCell className="font-medium">{item.company_name}</TableCell>
                    <TableCell>{item.contact_name}</TableCell>
                    <TableCell className="text-sm text-[#6B7C93]">{item.email}</TableCell>
                    <TableCell className="text-sm">{item.phone}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-1 rounded-full ${st.color}`}>{st.label}</span></TableCell>
                    <TableCell className="text-sm text-[#6B7C93]">{new Date(item.created_at).toLocaleDateString('fr-FR')}</TableCell>
                    <TableCell>
                      {item.status === 'pending' && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleApprove(item.id)}
                            disabled={processing === item.id}
                            className="text-green-600 hover:text-green-800 hover:bg-green-50"
                            data-testid={`approve-${item.id}`}
                          >
                            {processing === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleReject(item.id)}
                            disabled={processing === item.id}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            data-testid={`reject-${item.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!loading && items.length === 0 && (
            <div className="text-center py-12 text-[#6B7C93]">
              <BookmarkCheck className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Aucune réservation</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
